import { useState } from 'react';
import { api, fmtDate, fmtUsd } from '../api';
import { useAuth } from '../auth';
import { Badge, Button, Card, Empty, ErrorBox, Field, Flash, inputCls, Loading, PageHeader, StateBadge, Table, useAction, useApi } from '../components/ui';
import { AbmPlan } from '../components/Deliverables';

export function Campaigns() {
  const { data, error, loading } = useApi<any[]>('/api/campaigns');
  return (
    <>
      <PageHeader title="Campanhas" subtitle="Campanhas de arranque C1–C6. Orquestração completa (W07) entra na Fase 2; peças do MVP já podem ser vinculadas." />
      <ErrorBox error={error} />
      {loading && !data ? (
        <Loading />
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {data?.map((c) => (
            <Card key={c.id} title={`${c.id} — ${c.name}`} actions={<StateBadge state={c.status} />}>
              <p className="text-sm">{c.objective}</p>
              <p className="mt-2 text-xs text-slate-600">
                UTM: <code>{c.utmCampaign}</code> · peças vinculadas: {c.assets} · orçamento: {c.budgetBRL ? `R$ ${c.budgetBRL}` : 'não aprovado'}
              </p>
              <p className="text-xs text-slate-600">Critério de encerramento: {c.endCriteria}</p>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

export function NotActive({ title, phase, agents, what }: { title: string; phase: string; agents: string; what: string }) {
  return (
    <>
      <PageHeader title={title} subtitle={`${phase} · agentes ${agents}`} />
      <Card>
        <p className="text-sm">
          <Badge tone="slate">Não ativo no MVP</Badge> {what}
        </p>
        <p className="mt-2 text-sm text-slate-600">
          Esta área está planejada e documentada (agents/registry.yaml e workflows/), mas não executa nada ainda: nenhum botão finge funcionar. A ativação depende das
          decisões e integrações listadas em docs/integracoes-e-limites.md.
        </p>
      </Card>
    </>
  );
}

export function Abm() {
  const { can } = useAuth();
  const leads = useApi<any[]>(can('analytics:read') ? '/api/leads' : null);
  const plan = useApi<{ runId: string; at: string; output: any } | null>(can('analytics:read') ? '/api/abm/plan' : null);
  const act = useAction();
  const [f, setF] = useState({ email: '', name: '', company: '', consent: false });
  const [p, setP] = useState({ objective: 'Gerar reuniões de diagnóstico com contas-alvo', vertical: '', persona: 'CIO', offer: '', region: 'São Paulo', accounts: '' });
  return (
    <>
      <PageHeader title="ABM & Leads" subtitle="A06 monta o plano ABM (validação do gerente comercial); A15 classifica e roteia cada lead novo. Nada é enviado a pessoas ou ao CRM automaticamente." />
      <ErrorBox error={act.error ?? leads.error} />
      <Flash message={act.message} />
      {can('analytics:read') && (
        <Card title="Plano ABM (A06)" className="mb-4">
          <form
            className="grid gap-2 md:grid-cols-3"
            onSubmit={(e) => {
              e.preventDefault();
              void act
                .run(
                  () =>
                    api('/api/abm/plan', {
                      method: 'POST',
                      body: { objective: p.objective, vertical: p.vertical, persona: p.persona, offer: p.offer, region: p.region, knownAccounts: p.accounts.split(/\n|,/).map((s) => s.trim()).filter(Boolean), constraints: [] },
                    }),
                  'Plano ABM gerado.',
                )
                .then(plan.reload);
            }}
          >
            <Field label="Vertical" required>
              <input className={inputCls} value={p.vertical} onChange={(e) => setP({ ...p, vertical: e.target.value })} placeholder="Ex.: Saúde, Logística, Indústria" required />
            </Field>
            <Field label="Persona" required>
              <input className={inputCls} value={p.persona} onChange={(e) => setP({ ...p, persona: e.target.value })} required />
            </Field>
            <Field label="Região" required>
              <input className={inputCls} value={p.region} onChange={(e) => setP({ ...p, region: e.target.value })} required />
            </Field>
            <Field label="Oferta" required>
              <input className={inputCls} value={p.offer} onChange={(e) => setP({ ...p, offer: e.target.value })} placeholder="Ex.: Wi-Fi hospitalar com site survey" required />
            </Field>
            <Field label="Objetivo" required>
              <input className={inputCls} value={p.objective} onChange={(e) => setP({ ...p, objective: e.target.value })} required />
            </Field>
            <Field label="Contas conhecidas (opcional, uma por linha)">
              <textarea className={inputCls} rows={2} value={p.accounts} onChange={(e) => setP({ ...p, accounts: e.target.value })} placeholder="Só contas que a equipe autorizou" />
            </Field>
            <div className="md:col-span-3">
              <Button type="submit" disabled={act.busy}>
                {act.busy ? 'Gerando…' : 'Gerar plano ABM'}
              </Button>
              <span className="ml-2 text-xs text-slate-600">O A06 não inventa empresas: contas só da lista acima.</span>
            </div>
          </form>
          {plan.data && (
            <div className="mt-4 border-t border-slate-200 pt-3">
              <p className="text-xs text-slate-500">Último plano: {fmtDate(plan.data.at)} · validar com o gerente comercial antes de executar.</p>
              <AbmPlan o={plan.data.output} />
            </div>
          )}
        </Card>
      )}
      <div className="grid gap-4 lg:grid-cols-3">
        {can('request:create') && (
          <Card title="Registrar lead (manual)">
            <form
              className="space-y-2"
              onSubmit={(e) => {
                e.preventDefault();
                void act
                  .run(async () => {
                    const r = await api<{ deduplicated: boolean }>('/api/leads', { method: 'POST', body: { email: f.email, name: f.name, company: f.company, source: 'manual', consent: { marketing: f.consent, capturedAt: new Date().toISOString(), text: 'Consentimento registrado manualmente pelo usuário do sistema.' } } });
                    act.run(async () => undefined, r.deduplicated ? 'Lead já existia: deduplicado (+1 toque).' : 'Lead criado.');
                  })
                  .then(leads.reload);
              }}
            >
              <Field label="E-mail" required>
                <input className={inputCls} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required />
              </Field>
              <Field label="Nome" required>
                <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required />
              </Field>
              <Field label="Empresa" required>
                <input className={inputCls} value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} required />
              </Field>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} /> Consentimento de marketing obtido
              </label>
              <Button type="submit" disabled={act.busy}>
                Registrar
              </Button>
            </form>
          </Card>
        )}
        <Card title="Leads" className="lg:col-span-2">
          {can('analytics:read') ? (
            <Table head={['E-mail', 'Empresa', 'Estágio', 'Roteamento (A15)', 'Origem', 'Toques', 'Criado']} empty={!leads.data?.length}>
              {leads.data?.map((l) => (
                <tr key={l.id}>
                  <td className="px-3 py-2">{l.emailNormalized}</td>
                  <td className="px-3 py-2">{l.company}</td>
                  <td className="px-3 py-2">{l.stage}</td>
                  <td className="px-3 py-2 text-xs">{l.routing ? `Tier ${l.routing.tier} → ${l.routing.route === 'comercial' ? 'comercial' : l.routing.route === 'nutricao' ? 'nutrição' : 'revisar'}` : '—'}</td>
                  <td className="px-3 py-2">{l.source}</td>
                  <td className="px-3 py-2">{l.touches}</td>
                  <td className="px-3 py-2 text-xs">{fmtDate(l.createdAt)}</td>
                </tr>
              ))}
            </Table>
          ) : (
            <Empty>Seu papel não visualiza leads.</Empty>
          )}
          <p className="mt-2 text-xs text-slate-600">CRM oficial: WeSales (integração em preparação). Até lá os leads ficam na base interna; o A15 sugere estágio (no máximo MQL) e avisa o comercial nos leads de tier A.</p>
        </Card>
      </div>
    </>
  );
}

export function Analytics() {
  const { data, error, loading } = useApi<any>('/api/analytics');
  if (loading && !data) return <Loading />;
  if (!data) return <ErrorBox error={error} />;
  const o = data.operations;
  const fmt = (v: number | null) => (v === null ? 'indisponível' : v);
  return (
    <>
      <PageHeader title="Analytics / BI" subtitle="Métricas indisponíveis aparecem como indisponíveis — nunca como zero. Modelos de atribuição são análises separadas." />
      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Métricas de canal">
          <Table head={['Métrica', 'Valor', 'Fonte', 'Coleta']}>
            {data.channel.map((m: any) => (
              <tr key={m.metric}>
                <td className="px-3 py-2">{m.label}</td>
                <td className="px-3 py-2">{m.value === null ? <Badge title={m.unavailableReason}>indisponível</Badge> : m.value}</td>
                <td className="px-3 py-2 text-xs">{m.value === null ? m.unavailableReason : m.source}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(m.collectedAt)}</td>
              </tr>
            ))}
          </Table>
        </Card>
        <Card title="Operação (semana corrente)">
          <dl className="grid grid-cols-2 gap-2 text-sm">
            {[
              ['Demandas criadas', o.requestsCreated],
              ['Peças publicadas', o.assetsPublished],
              ['Aguardando aprovação', o.assetsPendingApproval],
              ['Bloqueadas', o.assetsBlocked],
              ['Tempo médio até aprovação (h)', fmt(o.avgApprovalHours)],
              ['Taxa de retrabalho (%)', fmt(o.reworkRatePct)],
              ['Falhas de publicação', o.publishFailures],
              ['Execuções de agente com falha', o.failedAgentRuns],
              ['Custo de IA', `${fmtUsd(o.aiCostUsd)}${o.aiCostIsEstimate ? ' (estimado)' : ''}`],
            ].map(([k, v]) => (
              <div key={k as string}>
                <dt className="text-xs text-slate-500">{k}</dt>
                <dd className="font-semibold">{String(v)}</dd>
              </div>
            ))}
          </dl>
        </Card>
        <Card title="Atribuição de pipeline" className="lg:col-span-2">
          <div className="grid gap-4 md:grid-cols-4">
            {data.attribution.map((m: any) => (
              <div key={m.model}>
                <h3 className="text-xs font-semibold uppercase text-slate-500">{m.model}</h3>
                {m.rows.length ? (
                  <ul className="text-sm">
                    {m.rows.map((r: any) => (
                      <li key={r.channel}>
                        {r.channel}: R$ {r.amountBRL.toLocaleString('pt-BR')} ({r.opportunities} opp.)
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-slate-500">Sem oportunidades atribuídas (CRM não conectado).</p>
                )}
                <p className="mt-1 text-[11px] text-slate-600">{m.note}</p>
              </div>
            ))}
          </div>
        </Card>
        <Card title="Custo de IA por agente">
          <Table head={['Agente', 'Total', 'Tipo']} empty={!data.aiCostByAgent.length}>
            {data.aiCostByAgent.map((c: any) => (
              <tr key={c.agentId}>
                <td className="px-3 py-2">{c.agentId}</td>
                <td className="px-3 py-2">{fmtUsd(c.total)}</td>
                <td className="px-3 py-2 text-xs">{c.estimate ? 'estimado (mock)' : 'real'}</td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>
    </>
  );
}

const ROLE_OPTIONS: [string, string][] = [
  ['CEO', 'CEO'],
  ['MARKETING_SPONSOR', 'Sponsor de Marketing'],
  ['TECH_REVIEWER', 'Revisor Técnico'],
  ['SALES_MANAGER', 'Gerente Comercial'],
  ['FINANCE_LEGAL', 'Financeiro/Jurídico'],
  ['REQUESTER', 'Solicitante'],
  ['AUDITOR', 'Auditor'],
  ['SYSTEM_ADMIN', 'Admin de Sistema'],
];

/** Equipe real: criar, mudar papel, desativar e redefinir senha (senha temporária mostrada uma única vez). */
function UsersCard({ users, meId }: { users: { data: any[] | null; reload: () => Promise<void> }; meId?: string }) {
  const act = useAction();
  const [f, setF] = useState({ name: '', email: '', role: 'REQUESTER' });
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null);
  const humans = (users.data ?? []).filter((u) => u.kind === 'human');
  const services = (users.data ?? []).filter((u) => u.kind === 'service');
  const showSecret = (email: string, password: string) => setSecret({ email, password });

  return (
    <Card title="Usuários (equipe)" className="lg:col-span-3">
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      <form
        className="mb-4 grid gap-2 md:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          void act
            .run(async () => {
              const r = await api<{ email: string; temporaryPassword: string }>('/api/admin/users', { method: 'POST', body: f });
              showSecret(r.email, r.temporaryPassword);
              setF({ name: '', email: '', role: 'REQUESTER' });
            }, 'Usuário criado.')
            .then(users.reload);
        }}
      >
        <Field label="Nome" required>
          <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required minLength={3} />
        </Field>
        <Field label="E-mail corporativo" required>
          <input className={inputCls} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required placeholder="nome@3snetworks.com.br" />
        </Field>
        <Field label="Papel" required>
          <select className={inputCls} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
            {ROLE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>
                {l}
              </option>
            ))}
          </select>
        </Field>
        <div className="flex items-end">
          <Button type="submit" disabled={act.busy}>
            Criar usuário
          </Button>
        </div>
      </form>
      {secret && (
        <div role="status" className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-semibold text-amber-900">Senha temporária de {secret.email} — mostrada uma única vez.</p>
          <code className="mt-1 block text-base">{secret.password}</code>
          <p className="mt-1 text-xs text-slate-700">Entregue à pessoa por um canal seguro (pessoalmente ou Teams). No 1º login ela será obrigada a criar uma senha própria.</p>
          <div className="mt-2 flex gap-2">
            <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(secret.password)}>
              Copiar
            </Button>
            <Button variant="secondary" onClick={() => setSecret(null)}>
              Ocultar
            </Button>
          </div>
        </div>
      )}
      <Table head={['Nome', 'E-mail', 'Papel', 'Situação', 'Ações']} empty={!humans.length}>
        {humans.map((u) => (
          <tr key={u.id} className={u.active ? '' : 'opacity-60'}>
            <td className="px-3 py-2">
              {u.name} {u.email.endsWith('.test') && <Badge tone="amber">conta de teste</Badge>}
            </td>
            <td className="px-3 py-2 text-xs">{u.email}</td>
            <td className="px-3 py-2">
              <label className="sr-only" htmlFor={`role-${u.id}`}>
                Papel de {u.name}
              </label>
              <select
                id={`role-${u.id}`}
                className="rounded border border-slate-300 px-2 py-1 text-xs"
                value={u.role}
                disabled={u.id === meId || act.busy}
                onChange={(e) => void act.run(() => api(`/api/admin/users/${u.id}`, { method: 'PATCH', body: { role: e.target.value } }), 'Papel atualizado; sessões da pessoa foram encerradas.').then(users.reload)}
              >
                {ROLE_OPTIONS.map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </td>
            <td className="px-3 py-2">
              {u.active ? <Badge tone="green">ativo</Badge> : <Badge tone="red">desativado</Badge>} {u.mustChangePassword && <Badge tone="amber">senha temporária</Badge>}
            </td>
            <td className="px-3 py-2">
              <div className="flex flex-wrap gap-3 text-xs">
                {u.id !== meId && (
                  <button className="text-brand-700 underline" onClick={() => void act.run(() => api(`/api/admin/users/${u.id}`, { method: 'PATCH', body: { active: !u.active } }), u.active ? 'Usuário desativado.' : 'Usuário reativado.').then(users.reload)}>
                    {u.active ? 'desativar' : 'reativar'}
                  </button>
                )}
                <button
                  className="text-brand-700 underline"
                  onClick={() =>
                    void act
                      .run(async () => {
                        const r = await api<{ email: string; temporaryPassword: string }>(`/api/admin/users/${u.id}/reset-password`, { method: 'POST' });
                        showSecret(r.email, r.temporaryPassword);
                      }, 'Senha redefinida.')
                      .then(users.reload)
                  }
                >
                  redefinir senha
                </button>
              </div>
            </td>
          </tr>
        ))}
      </Table>
      <p className="mt-3 text-xs text-slate-600">
        Identidades de serviço dos agentes (sem login, nunca aprovam): {services.map((s) => s.email.split('@')[0].toUpperCase()).join(' · ')}
      </p>
    </Card>
  );
}

function DisplayTokens() {
  const tokens = useApi<any[]>('/api/admin/display-tokens');
  const act = useAction();
  const [name, setName] = useState('TV do staff');
  const [link, setLink] = useState<string | null>(null);
  return (
    <Card title="Painéis de TV (monitoramento)" className="lg:col-span-3">
      <p className="text-sm text-slate-700">
        Gera um link de exibição <strong>somente leitura</strong> para uma TV. O link abre apenas o painel (sem preços, e-mails ou prompts) e pode ser revogado a qualquer momento.
        Você também pode abrir o painel logado: <a className="text-brand-700 underline" href="/painel" target="_blank" rel="noreferrer">/painel</a>.
      </p>
      <ErrorBox error={act.error ?? tokens.error} />
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <Field label="Nome do painel">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Button
          disabled={act.busy || name.trim().length < 3}
          onClick={() =>
            act.run(async () => {
              const r = await api<{ token: string }>('/api/admin/display-tokens', { method: 'POST', body: { name } });
              setLink(`${window.location.origin}/painel?token=${r.token}`);
              await tokens.reload();
            })
          }
        >
          Gerar link da TV
        </Button>
      </div>
      {link && (
        <div role="status" className="mt-3 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
          <p className="font-semibold text-amber-900">Copie agora: este link não será mostrado de novo.</p>
          <code className="mt-1 block break-all text-xs">{link}</code>
          <div className="mt-2 flex gap-2">
            <Button variant="secondary" onClick={() => void navigator.clipboard?.writeText(link)}>Copiar link</Button>
            <Button variant="secondary" onClick={() => setLink(null)}>Ocultar</Button>
          </div>
          <p className="mt-2 text-xs text-slate-600">Na TV: abra o link no navegador e pressione F11 (tela cheia). O token sai da barra de endereço e fica salvo naquele navegador.</p>
        </div>
      )}
      <Table head={['Painel', 'Criado', 'Último acesso', 'Status', '']} empty={!tokens.data?.length}>
        {tokens.data?.map((t) => (
          <tr key={t.id}>
            <td className="px-3 py-2">{t.name}</td>
            <td className="px-3 py-2 text-xs">{fmtDate(t.createdAt)}</td>
            <td className="px-3 py-2 text-xs">{fmtDate(t.lastUsedAt)}</td>
            <td className="px-3 py-2">{t.revokedAt ? <Badge tone="red">revogado</Badge> : <Badge tone="green">ativo</Badge>}</td>
            <td className="px-3 py-2">
              {!t.revokedAt && (
                <button className="text-xs text-red-700 underline" onClick={() => act.run(() => api(`/api/admin/display-tokens/${t.id}`, { method: 'DELETE' }), 'Painel revogado.').then(tokens.reload)}>
                  revogar
                </button>
              )}
            </td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

export function Admin() {
  const { can, me } = useAuth();
  const integ = useApi<any[]>('/api/admin/integrations');
  const ks = useApi<any>('/api/admin/killswitch');
  const users = useApi<any[]>(can('user:manage') ? '/api/admin/users' : null);
  const audit = useApi<any[]>(can('audit:read') ? '/api/admin/audit' : null);
  const jobs = useApi<any[]>(can('audit:read') ? '/api/admin/jobs' : null);
  const incidents = useApi<any[]>(can('audit:read') ? '/api/incidents' : null);
  const act = useAction();
  const [reason, setReason] = useState('');
  const active = !!ks.data?.active;
  return (
    <>
      <PageHeader title="Admin / Segurança" subtitle="Integrações (sem exposição de segredos), kill switch, usuários, fila de jobs, incidentes e auditoria." />
      <ErrorBox error={act.error} />
      <Flash message={act.message} />
      <div className="grid gap-4 lg:grid-cols-3">
        <Card title="Kill switch (W14)" className={active ? 'border-red-400' : ''}>
          <p className="text-sm">
            Estado: {active ? <Badge tone="red">ATIVO</Badge> : <Badge tone="green">inativo</Badge>}
          </p>
          {active && <p className="mt-1 text-xs">Motivo: {ks.data.reason} · {fmtDate(ks.data.at)}</p>}
          {(can('killswitch:activate') || can('killswitch:release')) && (
            <div className="mt-3 space-y-2">
              <Field label={active ? 'Nota de liberação' : 'Motivo da pausa'} required>
                <input className={inputCls} value={reason} onChange={(e) => setReason(e.target.value)} />
              </Field>
              {!active && can('killswitch:activate') && (
                <Button variant="danger" disabled={act.busy || reason.length < 5} onClick={() => act.run(() => api('/api/admin/killswitch', { method: 'POST', body: { action: 'activate', reason } }), 'Kill switch ativado: envios externos pausados e incidente aberto.').then(() => { ks.reload(); incidents.reload(); setReason(''); })}>
                  Pausar todos os envios externos
                </Button>
              )}
              {active && (
                <Button disabled={act.busy || reason.length < 5 || !can('killswitch:release')} title={can('killswitch:release') ? '' : 'Somente o CEO libera'} onClick={() => act.run(() => api('/api/admin/killswitch', { method: 'POST', body: { action: 'release', reason } }), 'Kill switch liberado.').then(() => { ks.reload(); setReason(''); })}>
                  Liberar (CEO)
                </Button>
              )}
            </div>
          )}
        </Card>
        {(me?.user.role === 'CEO' || me?.user.role === 'SYSTEM_ADMIN') && <DisplayTokens />}
        <Card title="Integrações" className="lg:col-span-2">
          <Table head={['Integração', 'Tipo', 'Status', 'Limitações verificadas']}>
            {integ.data?.map((i) => (
              <tr key={i.id}>
                <td className="px-3 py-2">{i.name}</td>
                <td className="px-3 py-2 text-xs">{i.kind}</td>
                <td className="px-3 py-2">
                  <StateBadge state={i.status} />
                </td>
                <td className="px-3 py-2 text-xs">
                  {[i.note, ...(i.verifiedLimitations ?? [])].filter(Boolean).join(' · ')}
                </td>
              </tr>
            ))}
          </Table>
          <p className="mt-2 text-xs text-slate-600">
            Conexões são configuradas por variáveis de ambiente no servidor (ver .env.example). Não há botão de “conectar” enquanto a autorização não for concedida. Ambiente: {me?.env}.
          </p>
        </Card>
        {can('audit:read') && (
          <Card title="Incidentes" className="lg:col-span-3">
            {incidents.data?.length ? (
              <Table head={['ID', 'Título', 'Severidade', 'Status', 'Opções de resposta', 'Aberto']}>
                {incidents.data.map((i) => (
                  <tr key={i.id}>
                    <td className="px-3 py-2 font-mono text-xs">{i.id}</td>
                    <td className="px-3 py-2">{i.title}</td>
                    <td className="px-3 py-2">{i.severity}</td>
                    <td className="px-3 py-2">{i.status}</td>
                    <td className="px-3 py-2 text-xs">{(i.responseOptions as string[]).join(' | ')}</td>
                    <td className="px-3 py-2 text-xs">{fmtDate(i.createdAt)}</td>
                  </tr>
                ))}
              </Table>
            ) : (
              <Empty>Nenhum incidente.</Empty>
            )}
          </Card>
        )}
        {can('user:manage') && <UsersCard users={users} meId={me?.user.id} />}
        {can('audit:read') && (
          <>
            <Card title="Fila de jobs" className="lg:col-span-3">
              <Table head={['Job', 'Tipo', 'Status', 'Tentativas', 'Executar em', 'Erro', '']}>
                {jobs.data?.slice(0, 50).map((j) => (
                  <tr key={j.id}>
                    <td className="px-3 py-1 font-mono text-[11px]">{j.id}</td>
                    <td className="px-3 py-1 text-xs">{j.type}{j.external ? ' (externo)' : ''}</td>
                    <td className="px-3 py-1 text-xs">{j.status}</td>
                    <td className="px-3 py-1 text-xs">{j.attempts}/{j.maxAttempts}</td>
                    <td className="px-3 py-1 text-xs">{fmtDate(j.runAt)}</td>
                    <td className="px-3 py-1 text-xs text-red-800">{j.lastError}</td>
                    <td className="px-3 py-1">
                      {['QUEUED', 'RUNNING', 'PAUSED'].includes(j.status) && can('content:cancel') && (
                        <button className="text-xs text-red-700 underline" onClick={() => act.run(() => api(`/api/admin/jobs/${j.id}/cancel`, { method: 'POST' }), 'Cancelamento solicitado.').then(jobs.reload)}>
                          cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            </Card>
            <Card title="Trilha de auditoria (últimos 300)" className="lg:col-span-3">
              <ol className="max-h-96 space-y-1 overflow-auto text-xs">
                {audit.data?.map((e) => (
                  <li key={e.id} className="font-mono">
                    {fmtDate(e.at)} · {e.actorKind}:{e.actorId} · {e.action} · {e.entityType}:{e.entityId}
                  </li>
                ))}
              </ol>
            </Card>
          </>
        )}
      </div>
    </>
  );
}
